import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { EventQuery } from "lib/build/events";

import { Card, CardContent } from "src/common/components/Card";
import {
	ColumnProps,
	DataTable,
	DataTableSearch,
	DataTableTitle,
	DataTableToolbar,
	DataTableWrapper,
	EmptyDataTableMessage,
	RowCompareFn,
	RowData,
	SortDirection
} from "src/common/components/DataTable";
import {
	MobileColumnProps,
	MobileTable,
	MobileTableSearch,
	MobileTableSort,
	MobileTableTitle,
	MobileTableToolbar,
	MobileTableWrapper
} from "src/common/components/MobileTable";
import { ListPage } from "src/common/components/Page";
import { FullPagePreloader } from "src/common/components/Preloader";
import { useIsMobile } from "src/common/hooks/useIsMobile";
import { genericMessages } from "src/messages/generic";
import { useGoToPatient } from "src/modules/shared/patient-list/hooks/useGoToPatient";
import { usePatientList } from "src/modules/shared/patient-list/hooks/usePatientList";
import { messages } from "src/modules/shared/patient-list/messages";
import { RawPatient } from "src/modules/shared/patient-list/types";

interface PatientListProps<T> {
	queries: { [key: string]: EventQuery };
	mobileColumns: MobileColumnProps;
	desktopColumns: ColumnProps[];
	initialSortKey?: string;
	initialSortDirection?: SortDirection;
	normalizePatient(patient: RawPatient): T;
	preCompareFn?(a: T, b: T): number;
}

export function PatientList<T>({
	queries,
	mobileColumns,
	desktopColumns,
	initialSortKey,
	initialSortDirection,
	normalizePatient,
	preCompareFn
}: PatientListProps<T>): React.ReactElement {
	const isMobile = useIsMobile();
	return isMobile ? (
		<MobileContent
			queries={queries}
			columns={mobileColumns}
			normalizePatient={normalizePatient}
			preCompareFn={preCompareFn}
		/>
	) : (
		<DesktopContent
			queries={queries}
			columns={desktopColumns}
			initialSortKey={initialSortKey}
			initialSortDirection={initialSortDirection}
			normalizePatient={normalizePatient}
			preCompareFn={preCompareFn}
		/>
	);
}

interface MobileContentProps<T> {
	queries: { [key: string]: EventQuery };
	columns: MobileColumnProps;
	normalizePatient(patient: RawPatient): T;
	preCompareFn?(a: T, b: T): number;
}

function MobileContent<T>({
	queries,
	columns,
	normalizePatient,
	preCompareFn
}: MobileContentProps<T>): React.ReactElement {
	const { formatMessage } = useIntl();

	const queryList = React.useMemo(() => Object.values(queries), [queries]);
	const { isLoading, patients } = usePatientList(queryList, normalizePatient);

	const [query, setQuery] = React.useState("");
	const [hasChangedSortKey, setHasChangedSortKey] = React.useState(false);
	const [sortKey, setSortKey] = React.useState("name");
	const [sortDirection, setSortDirection] =
		React.useState<SortDirection>("asc");

	const goToPatient = useGoToPatient();

	if (isLoading) {
		return <FullPagePreloader />;
	}

	return (
		<ListPage>
			<MobileTableToolbar>
				<MobileTableTitle>
					<FormattedMessage {...messages.patientList} />
				</MobileTableTitle>
				<MobileTableSearch
					placeholder={formatMessage(genericMessages.searchOnPage)}
					query={query}
					setQuery={setQuery}
				/>
				<MobileTableSort
					columns={columns}
					sortKey={sortKey}
					setSortKey={key => {
						setHasChangedSortKey(true);
						setSortKey(key);
					}}
					sortDirection={sortDirection}
					setSortDirection={dir => {
						setHasChangedSortKey(true);
						setSortDirection(dir);
					}}
				/>
			</MobileTableToolbar>
			<MobileTableWrapper>
				<MobileTable
					columns={columns}
					data={patients as unknown as RowData[]}
					sortKey={sortKey}
					sortDirection={sortDirection}
					query={query}
					noDataMessage={
						<EmptyDataTableMessage>
							<FormattedMessage {...genericMessages.noMatches} />
						</EmptyDataTableMessage>
					}
					onRowClick={goToPatient}
					preCompareFn={
						hasChangedSortKey
							? undefined
							: (preCompareFn as RowCompareFn | undefined)
					}
				/>
			</MobileTableWrapper>
		</ListPage>
	);
}

interface DesktopContentProps<T> {
	queries: { [key: string]: EventQuery };
	columns: ColumnProps[];
	initialSortKey?: string;
	initialSortDirection?: SortDirection;
	normalizePatient(patient: RawPatient): T;
	preCompareFn?(a: T, b: T): number;
}

function DesktopContent<T>({
	queries,
	columns,
	initialSortKey,
	initialSortDirection,
	normalizePatient,
	preCompareFn
}: DesktopContentProps<T>): React.ReactElement {
	const { formatMessage } = useIntl();
	const { isLoading, patients } = usePatientList(
		Object.values(queries),
		normalizePatient
	);
	const [query, setQuery] = React.useState("");
	const goToPatient = useGoToPatient();
	if (isLoading) {
		return <FullPagePreloader />;
	}
	return (
		<ListPage>
			<Card>
				<CardContent>
					<DataTableToolbar>
						<DataTableTitle>
							<FormattedMessage {...messages.patientList} />
						</DataTableTitle>
						<DataTableSearch
							placeholder={formatMessage(
								genericMessages.searchOnPage
							)}
							query={query}
							setQuery={setQuery}
						/>
					</DataTableToolbar>
					<DataTableWrapper>
						<DataTable
							columns={columns}
							data={patients as unknown as RowData[]}
							isSticky={true}
							initialSortKey={
								initialSortKey ? initialSortKey : "name"
							}
							initialSortDirection={
								initialSortDirection
									? initialSortDirection
									: "asc"
							}
							query={query}
							noDataMessage={
								<EmptyDataTableMessage>
									<FormattedMessage
										{...genericMessages.noMatches}
									/>
								</EmptyDataTableMessage>
							}
							onRowClick={goToPatient}
							preCompareFn={
								preCompareFn as RowCompareFn | undefined
							}
						/>
					</DataTableWrapper>
				</CardContent>
			</Card>
		</ListPage>
	);
}
